import React, { useCallback } from 'react';
import { Users, Plus, CreditCard, BarChart3 } from 'lucide-react';
import { Challenge } from '@/types';
import { Button } from '@/components/ui/Button';
import { Card } from '@/components/ui/Card';
import { ChallengesSections } from '@/components/features/ChallengesSections';
import { PullToRefresh } from '@/components/ui/PullToRefresh';
import { useUserStore } from '@/stores/userStore';
import { useChallenges } from '@/hooks/useChallenges';
import { apiClient } from '@/lib/api';
import { useToast } from '@/components/ui/Toast';

interface HomeViewProps {
  challenges: Challenge[];
  isLoading?: boolean;
  onCreateChallenge: () => void;
  onJoinChallenge: () => void;
  onSelectChallenge: (challenge: Challenge) => void;
  onShowGymPass: () => void;
  onShowStats: () => void;
}

const HomeView: React.FC<HomeViewProps> = ({
  challenges,
  isLoading = false,
  onCreateChallenge,
  onJoinChallenge,
  onSelectChallenge,
  onShowGymPass,
  onShowStats,
}) => {
  const { user, logout } = useUserStore();
  const { refetch } = useChallenges();
  const { addToast } = useToast();

  const handleRefresh = useCallback(async () => {
    try {
      // Force sync with server and refetch challenges
      await refetch();

      addToast({
        type: 'success',
        message: 'Refreshed successfully!',
      });
    } catch (error) {
      console.error('Refresh failed:', error);
      addToast({
        type: 'error',
        message: 'Failed to refresh. Please try again.',
      });
    }
  }, [refetch, addToast]);

  return (
    <div className="max-w-md mx-auto min-h-screen">
      <PullToRefresh onRefresh={handleRefresh}>
        <div className="p-6">
          {/* Header */}
          <header className="text-center mb-8">
            <div className="pushing-pencils-title mb-3 mx-auto w-fit">
              <div className="w-20 h-20 bg-gradient-to-br from-blue-500 to-purple-600 rounded-2xl flex items-center justify-center shadow-xl overflow-hidden">
                <img
                  src="/logo.png"
                  alt="Pushing Pencils Logo"
                  className="w-full h-full object-cover"
                />
              </div>
              <h1 className="pushing-pencils-text">Pushing Pencils</h1>
            </div>
            <p className="text-slate-600 font-medium text-lg font-heading">
              "WHAT'S DA MATTER?" 💪
            </p>
            {user && (
              <div className="flex items-center justify-center gap-4 mt-2">
                <p className="text-sm text-blue-600">
                  Welcome back, <span className="font-semibold">{user.username}</span>!
                </p>
                <button
                  onClick={logout}
                  className="text-xs text-gray-700 hover:text-gray-900 underline"
                  aria-label="Logout from account"
                >
                  Logout
                </button>
              </div>
            )}
          </header>

          {/* Action Buttons */}
          <nav aria-label="Primary actions" className="space-y-3 mb-6">
            <Button
              onClick={onCreateChallenge}
              variant="primary"
              className="w-full h-12"
              leftIcon={<Plus size={20} />}
            >
              Create Challenge
            </Button>

            <Button
              onClick={onJoinChallenge}
              variant="success"
              className="w-full h-12"
              leftIcon={<Users size={20} />}
            >
              Join Challenge
            </Button>
          </nav>

          {/* Gym Pass Button */}
          <div className="mb-6">
            <Button
              onClick={onShowGymPass}
              variant="secondary"
              className="w-full h-12"
              leftIcon={<CreditCard size={20} />}
            >
              View My Gym Pass
            </Button>
          </div>

          {/* Stats Button */}
          <div className="mb-6">
            <Button
              onClick={onShowStats}
              variant="outline"
              className="w-full h-12 border-neon-yellow text-neon-yellow hover:bg-neon-yellow hover:text-black"
              leftIcon={<BarChart3 size={20} />}
            >
              📊 View Stats
            </Button>
          </div>

          {/* My Challenges */}
          {(challenges.length > 0 || isLoading) && (
            <section aria-label="My challenges">
              <ChallengesSections challenges={challenges} onSelectChallenge={onSelectChallenge} />
            </section>
          )}

          {/* Empty State */}
          {!isLoading && challenges.length === 0 && (
            <Card className="text-center p-8 mt-8">
              <div className="text-6xl mb-4">💪</div>
              <h3 className="text-lg font-semibold text-gray-700 mb-2">
                Ready to Start Your Fitness Journey?
              </h3>
              <p className="text-gray-500 mb-6">
                Create your first challenge or join one with friends to get started!
              </p>
              <div className="space-y-3">
                <Button onClick={onCreateChallenge} className="w-full">
                  Create Your First Challenge
                </Button>
                <Button onClick={onJoinChallenge} variant="outline" className="w-full">
                  Join a Friend's Challenge
                </Button>
              </div>
            </Card>
          )}
        </div>
      </PullToRefresh>
    </div>
  );
};

export { HomeView };
